// /src/GuitarSimulator.js

import React, { useEffect, useRef, useState, useCallback } from 'react';
import './GuitarSimulator.css';
import { recordPageVisit } from './utils/pageVisitTracker';

// 모듈 imports
import { AudioAnalyzer, AudioUtils } from './GuitarSimulator/audioAnalyzer';
import { chordDefinitions, chordCategories, alternativeChordPositions } from './GuitarSimulator/chordDefinitions';
import { GuitarUtils, stringNotes, frequencies } from './GuitarSimulator/guitarUtils';
import { GuitarCanvas } from './GuitarSimulator/GuitarCanvas';
import ExternalInputControls from './GuitarSimulator/ExternalInputControls';
import FrequencySpectrumDisplay from './GuitarSimulator/FrequencySpectrumDisplay';

const GuitarSimulator = ({ language, user, countryCode, userIp }) => {
  // Refs
  const canvasRef = useRef(null);
  const audioContextRef = useRef(null);
  const masterGainRef = useRef(null);
  const currentOscillatorRef = useRef(null);
  const currentGainNodeRef = useRef(null);
  const guitarCanvasRef = useRef(null);

  // 기존 States
  const [isAudioReady, setIsAudioReady] = useState(false);
  const [showNotes, setShowNotes] = useState(true);
  const [selectedChord, setSelectedChord] = useState(null);
  const [volume, setVolume] = useState(50);
  const [noteDisplay, setNoteDisplay] = useState(
    language === 'KR' 
      ? '현을 누르거나, 코드를 선택해 보세요.' 
      : 'Touch a string, or select a chord.'
  );
  const [pressedFret, setPressedFret] = useState(null);
  const [pressedString, setPressedString] = useState(null);
  const [isMouseDown, setIsMouseDown] = useState(false);
  const [isShiftPressed, setIsShiftPressed] = useState(false);
  const [lastMousePosition, setLastMousePosition] = useState({ x: 0, y: 0 });
  const [activeChordCategory, setActiveChordCategory] = useState('basic');
  const [audioStatus, setAudioStatus] = useState('오디오를 시작하려면 버튼을 클릭하세요');
  const [canvasTransform, setCanvasTransform] = useState({
    rotation: 180,
    flipX: false,
    flipY: false
  });
  const [useKoreanNotes, setUseKoreanNotes] = useState(false); // 한글 음계 표시 여부
  const [showFingerNumbers, setShowFingerNumbers] = useState(false); // 손가락 번호 표시 여부
  const [chordPlayMode, setChordPlayMode] = useState('primary'); 
  const [chordPlayPattern, setChordPlayPattern] = useState('travis'); // 새로 추가

  // 새로운 States (외부 입력 관련)
  const [audioAnalyzer, setAudioAnalyzer] = useState(null);
  const [isExternalInput, setIsExternalInput] = useState(false);
  const [noteIntensities, setNoteIntensities] = useState({});
  const [inputSource, setInputSource] = useState('none'); // 'microphone', 'file', 'none'
  const [showSpectrum, setShowSpectrum] = useState(false); // 스펙트럼 표시 여부

  const SERVER_ROOT = process.env.REACT_APP_SERVER_ROOT || 'http://localhost:3001';
  const userId = user ? user.uid : 'unknown';
  const isMobileDevice = GuitarUtils.isMobile();
  const MAXFRET = GuitarUtils.getMaxFret(isMobileDevice);
  const [chordPlaySpeed, setChordPlaySpeed] = useState(300);
  const [soundType, setSoundType] = useState('guitar'); // 'guitar', 'piano', 'violin', 'organ', 'synth'
  const [chordPlayStyle, setChordPlayStyle] = useState({
    sequential: true,        // 순차 연주 (기본)
    arpeggio: false,         // 기본 아르페지오 (6-5-4-3-2-1)
    reverseArpeggio: false,  // 역순 아르페지오 (1-2-3-4-5-6)
    classicPIMA: false,      // 클래식 P-I-M-A (6-3-2-1)
    classicPIMAMI: false,    // 클래식 P-I-M-A-M-I (6-3-2-1-2-3)
    classicPMIM: false,      // 클래식 P-M-I-M (6-2-3-2)
    travisPicking: false,    // Travis Picking (6-3-2-3-5-3-2-3)
    carterStyle: false,      // Carter Style (6-2-5-1-4-3)
    folkPattern: false,      // Folk Pattern (6-4-3-2-3-4)
    forwardRoll: false,      // Forward Roll (5-3-1-2)
    backwardRoll: false,     // Backward Roll (2-1-3-5)
    mixedRoll: false,        // Mixed Roll (5-1-2-3)
    sweepUp: false,          // 상행 스위프 (6→1 빠르게)
    sweepDown: false,        // 하행 스위프 (1→6 빠르게)
    sweepBoth: false,        // 양방향 스위프 (6→1→6)
    pattern33: false,        // 3-3 패턴 (6-5-4, 3-2-1)
    pattern222: false,       // 2-2-2 패턴 (6-5, 4-3, 2-1)
    brokenChord: false,      // Broken Chord (불규칙)
    flamenco: false,         // 플라멩코 라스게아도
    bossaNova: false,        // 보사노바 (6-2-5-1-4-3)
    strum: false,            // 스트러밍 (동시 연주)
    swing: false             // 스윙 (저음부-고음부)
  });
  const getBPM = useCallback(() => {
    return Math.round(60000 / chordPlaySpeed);
  }, [chordPlaySpeed]);
  // SERVER_ROOT 정의 바로 아래에 추가:
  const getSoundConfig = useCallback((type) => {
    const configs = {
      guitar: {
        waveform: 'sawtooth',
        frequencyMultiplier: 1,
        filterType: 'lowpass',
        filterFrequency: 3000,
        filterQ: 1,
        attack: 0.8,
        attackTime: 0.01,
        sustain: 0.3,
        decayTime: 0.1,
        defaultDuration: 1.5
      },
      piano: {
        waveform: 'triangle',
        frequencyMultiplier: 1,
        filterType: 'lowpass',
        filterFrequency: 8000,
        filterQ: 0.5,
        attack: 0.9,
        attackTime: 0.005,
        sustain: 0.1,
        decayTime: 0.3,
        defaultDuration: 2.0
      },
      violin: {
        waveform: 'sawtooth',
        frequencyMultiplier: 1,
        filterType: 'bandpass',
        filterFrequency: 2000,
        filterQ: 3,
        attack: 0.7,
        attackTime: 0.1,
        sustain: 0.6,
        decayTime: 0.05,
        defaultDuration: 2.5
      },
      organ: {
        waveform: 'square',         // sine → square (오르간의 특징적인 배음)
        frequencyMultiplier: 1,
        filterType: 'lowpass',      // highpass → lowpass 
        filterFrequency: 2500,      // 100 → 2500 (적절한 밝기)
        filterQ: 0.5,
        attack: 0.9,               // 즉시 최대음량 도달
        attackTime: 0.001,         // 0.02 → 0.001 (거의 즉시)
        sustain: 0.8,              // 0.7 → 0.8 (지속음 특성)
        decayTime: 0.001,          // 0.02 → 0.001 (즉시 서스테인으로)
        defaultDuration: 3.0
      },
      synth: {
        waveform: 'square',
        frequencyMultiplier: 1,
        filterType: 'lowpass',
        filterFrequency: 1500,
        filterQ: 2,
        attack: 0.6,
        attackTime: 0.03,
        sustain: 0.4,
        decayTime: 0.2,
        defaultDuration: 1.8
      },
      acoustic: {
        waveform: 'sawtooth',
        frequencyMultiplier: 1,
        filterType: 'bandpass',
        filterFrequency: 1200,
        filterQ: 0.5,
        attack: 0.7,
        attackTime: 0.028,
        sustain: 0.3,
        decayTime: 0.5,
        defaultDuration: 4
      }
    };
    return configs[type] || configs.guitar;
  }, []);

  // 페이지 방문 기록
  useEffect(() => {
    if (countryCode && countryCode !== 'UNKNOWN') {
      recordPageVisit('GuitarSimulator', {
        userId,
        userEmail: user ? user.email : 'unknown',
        userIp,
        countryCode,
        SERVER_ROOT,
        language,
        isMobileDevice
      });
    }
  }, [countryCode]);

  // 오디오 초기화
  const initAudio = useCallback(async () => {
    try {
      const context = new (window.AudioContext || window.webkitAudioContext)();

      if (context.state === 'suspended') {
        await context.resume();
      }

      const gain = context.createGain();
      gain.connect(context.destination);
      gain.gain.value = 0.3;

      audioContextRef.current = context;
      masterGainRef.current = gain;
      setIsAudioReady(true);
      setAudioStatus('✅ 오디오 시스템이 준비되었습니다!');

      console.log('오디오 초기화 완료');
      return true;
    } catch (error) {
      console.error('오디오 초기화 실패:', error);
      setAudioStatus('❌ 오디오 시스템 초기화에 실패했습니다');
      return false;
    }
  }, []);

  // 외부 입력 관련 함수들
  const initMicrophoneInput = useCallback(async () => {
    try {
      const analyzer = new AudioAnalyzer(audioContextRef.current); // 기존 AudioContext 전달
      const success = await analyzer.initMicrophone();
      
      if (success) {
        setAudioAnalyzer(analyzer);
        setInputSource('microphone');
        setIsExternalInput(true);
        
        analyzer.start();
        analyzer.startAnalysisLoop((intensities) => {
          setNoteIntensities(intensities);
        });
        
        console.log('마이크 입력 시작됨');
      }
    } catch (error) {
      console.error('마이크 초기화 오류:', error);
      alert(error.message || (language === 'KR' ? '마이크 접근 권한이 필요합니다.' : 'Microphone access permission required.'));
    }
  }, [language]);

  // MP3 파일과 기타 사운드 분리를 위한 별도 오디오 컨텍스트
  const initFileInput = useCallback(async (file) => {
    try {
      const analyzer = new AudioAnalyzer(); // 파일 분석용은 별도 컨텍스트 사용
      const success = await analyzer.initFileAnalysis(file);
      
      if (success) {
        setAudioAnalyzer(analyzer);
        setInputSource('file');
        setIsExternalInput(true);
        
        analyzer.start();
        analyzer.startAnalysisLoop((intensities) => {
          setNoteIntensities(intensities);
        });
        
        // 기타 사운드용 오디오 컨텍스트가 정지되지 않도록 보장
        if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
          audioContextRef.current.resume();
        }
        
        console.log('파일 분석 시작됨');
        
        // 파일이 자동 재생되지 않았다면 사용자에게 알림
        setTimeout(() => {
          if (analyzer.audioElement && analyzer.audioElement.paused) {
            alert(language === 'KR' ? 
              '파일이 로드되었습니다. 재생 버튼을 눌러 음악을 시작하세요.' : 
              'File loaded. Click play button to start the music.');
          }
        }, 1000);
      }
    } catch (error) {
      console.error('파일 분석 오류:', error);
      alert(error.message || (language === 'KR' ? '파일 로드에 실패했습니다.' : 'Failed to load file.'));
    }
  }, [language]);

  const stopExternalInput = useCallback(() => {
    if (audioAnalyzer) {
      audioAnalyzer.stop();
      setAudioAnalyzer(null);
    }
    setIsExternalInput(false);
    setInputSource('none');
    setNoteIntensities({});
    setShowSpectrum(false);
    
    // 기타 사운드용 오디오 컨텍스트 재개
    if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
      audioContextRef.current.resume();
    }
  }, [audioAnalyzer]);

  const handleMicrophoneToggle = useCallback(() => {
    if (inputSource === 'microphone') {
      stopExternalInput();
    } else {
      initMicrophoneInput();
    }
  }, [inputSource, stopExternalInput, initMicrophoneInput]);

  // 음표 재생 관련 함수들 
  const startSound = useCallback((note, stringIndex = 0) => {
    if (!isAudioReady || !audioContextRef.current || !masterGainRef.current) return;

    const frequency = frequencies[note];
    if (!frequency) return;

    stopSound();

    try {
      const oscillator = audioContextRef.current.createOscillator();
      const gainNode = audioContextRef.current.createGain();
      const filterNode = audioContextRef.current.createBiquadFilter();

      const soundConfig = getSoundConfig(soundType);
      oscillator.type = soundConfig.waveform;
      oscillator.frequency.value = frequency * soundConfig.frequencyMultiplier;
      
      filterNode.type = soundConfig.filterType;
      filterNode.frequency.value = soundConfig.filterFrequency;
      filterNode.Q.value = soundConfig.filterQ;

      oscillator.connect(filterNode);
      filterNode.connect(gainNode);
      gainNode.connect(masterGainRef.current);

      const now = audioContextRef.current.currentTime;
      gainNode.gain.setValueAtTime(0, now);
      gainNode.gain.linearRampToValueAtTime(soundConfig.attack, now + soundConfig.attackTime);
      gainNode.gain.linearRampToValueAtTime(soundConfig.sustain, now + soundConfig.attackTime + soundConfig.decayTime);

      oscillator.start(now);

      currentOscillatorRef.current = oscillator;
      currentGainNodeRef.current = gainNode;

      // 현 진동 효과 시작 (지속음이므로 긴 시간)
      if (guitarCanvasRef.current) {
        guitarCanvasRef.current.startStringVibration(stringIndex, 100); // 3초로 단축
      }

    } catch (error) {
      console.error('사운드 시작 오류:', error);
    }
  }, [isAudioReady, soundType]);

  const stopSound = useCallback(() => {
    if (currentOscillatorRef.current && currentGainNodeRef.current) {
      try {
        const now = audioContextRef.current.currentTime;
        const releaseTime = 0.5;

        currentGainNodeRef.current.gain.cancelScheduledValues(now);
        currentGainNodeRef.current.gain.exponentialRampToValueAtTime(0.001, now + releaseTime);

        currentOscillatorRef.current.stop(now + releaseTime);

        // 현 진동 중지
        if (guitarCanvasRef.current) {
          guitarCanvasRef.current.stopAllVibrations();
        }

        setTimeout(() => {
          currentOscillatorRef.current = null;
          currentGainNodeRef.current = null;
        }, releaseTime * 1000);
      } catch (error) {
        console.error('사운드 정지 오류:', error);
        currentOscillatorRef.current = null;
        currentGainNodeRef.current = null;
      }
    }
  }, []);

  // 기존 playSound 함수를 다음과 같이 교체:
  const playSound = useCallback((note, stringIndex = 0, duration = null) => {
    if (!isAudioReady || !audioContextRef.current || !masterGainRef.current) return;

    const frequency = frequencies[note];
    if (!frequency) return;

    try {
      const oscillator = audioContextRef.current.createOscillator();
      const gainNode = audioContextRef.current.createGain();
      const filterNode = audioContextRef.current.createBiquadFilter();

      const soundConfig = getSoundConfig(soundType);
      oscillator.type = soundConfig.waveform;
      oscillator.frequency.value = frequency * soundConfig.frequencyMultiplier;
      
      filterNode.type = soundConfig.filterType;
      filterNode.frequency.value = soundConfig.filterFrequency;
      filterNode.Q.value = soundConfig.filterQ;

      oscillator.connect(filterNode);
      filterNode.connect(gainNode);
      gainNode.connect(masterGainRef.current);

      const releaseTime = duration || soundConfig.defaultDuration;
      const now = audioContextRef.current.currentTime;

      // ADSR 엔벨로프
      gainNode.gain.setValueAtTime(0, now);
      gainNode.gain.linearRampToValueAtTime(soundConfig.attack, now + soundConfig.attackTime);
      gainNode.gain.linearRampToValueAtTime(soundConfig.sustain, now + soundConfig.attackTime + soundConfig.decayTime);
      gainNode.gain.exponentialRampToValueAtTime(0.001, now + soundConfig.attackTime + soundConfig.decayTime + releaseTime);

      oscillator.start(now);
      oscillator.stop(now + soundConfig.attackTime + soundConfig.decayTime + releaseTime);

      // 현 진동 효과 시작
      if (guitarCanvasRef.current) {
        const vibrationDuration = Math.min(100, (releaseTime + soundConfig.attackTime + soundConfig.decayTime) * 800); // 최대 1.5초, 계수 0.8 적용
        guitarCanvasRef.current.startStringVibration(stringIndex, vibrationDuration);
      }

    } catch (error) {
      console.error('사운드 재생 오류:', error);
    }
  }, [isAudioReady, soundType]);

  const getChordPlayPattern = useCallback((pattern) => {
    const patterns = {
      // === 기본 패턴 ===
      'sequential': [0, 1, 2, 3, 4, 5], // 6현→1현
      'reverse': [5, 4, 3, 2, 1, 0], // 1현→6현
      'updown': [0, 1, 2, 3, 4, 5, 4, 3, 2, 1], // 6현→1현→6현
      
      // === 트래비스 피킹 (Merle Travis 스타일) ===
      // 핵심: 엄지로 교대 베이스, 손가락으로 멜로디
      'travis_basic': [0, 4, 1, 4, 2, 4, 1, 4], // 6현-2현-5현-2현-4현-2현-5현-2현 (기본 패턴)
      'travis_classic': [0, 4, 1, 3, 0, 5, 1, 4], // 6현-2현-5현-3현-6현-1현-5현-2현 (클래식)
      'travis_simple': [0, 4, 1, 4], // 6현-2현-5현-2현 (단순 버전)
      'travis_pinch': [0, 4, 1, 4, 0, 3, 1, 4], // 핀치 포함 버전
      
      // === 아르페지오 패턴 ===
      'arpeggio_up': [0, 2, 3, 4, 5], // 6현→4현→3현→2현→1현 (상승)
      'arpeggio_down': [5, 4, 3, 2, 0], // 1현→2현→3현→4현→6현 (하강)
      'arpeggio_roll': [0, 3, 4, 5, 4, 3], // 6현→3현→2현→1현→2현→3현 (롤링)
      
      // === PIMA 패턴 (P=엄지, I=검지, M=중지, A=약지) ===
      'pima_basic': [0, 3, 4, 5], // P-I-M-A (6현-3현-2현-1현)
      'pima_reverse': [5, 4, 3, 0], // A-M-I-P (1현-2현-3현-6현)
      'pima_alternating': [0, 3, 4, 5, 1, 3, 4, 5], // 교대 베이스 포함
      
      // === 장르별 특화 패턴 ===
      'waltz': [0, 4, 3, 1, 5, 3, 2, 4, 3], // 왈츠 3/4 박자 (강-약-약)
      'country': [0, 1, 4, 3, 0, 2, 4, 3], // 컨트리 스타일
      'folk': [0, 3, 1, 4, 0, 2, 1, 3], // 포크 스타일
      'blues': [0, 4, 1, 3, 2, 5, 1, 4], // 블루스 스타일
      'ballad': [0, 5, 4, 3, 1, 4, 2, 3], // 발라드 스타일
      'classical': [0, 3, 4, 5, 4, 3, 2, 4], // 클래식 기타 스타일
      
      // === 고급 패턴 ===
      'alternating_bass': [0, 4, 2, 5, 1, 3], // 교대 베이스
      'syncopated': [0, 4, 5, 1, 4, 3], // 싱코페이션
      'celtic': [0, 3, 4, 1, 5, 2, 3, 4], // 켈틱 스타일
      'flamenco': [0, 5, 4, 3, 2, 4, 5], // 플라멩코 스타일
      
      // === 스트러밍 ===
      'strum_down': [0, 1, 2, 3, 4, 5], // 다운 스트럼
      'strum_updown': [0, 1, 2, 3, 4, 5, 5, 4, 3, 2, 1, 0], // 업다운 스트럼
      'strum_folk': [0, 1, 2, 3, 4, 5, 4, 3, 2, 1] // 포크 스트럼
    };
    return patterns[pattern] || patterns['sequential'];
  }, []);

  const playChord = useCallback((chordName) => {
    if (!isAudioReady || !chordDefinitions[chordName]) {
      console.log(`코드 정의를 찾을 수 없습니다: ${chordName}`);
      return;
    }

    let chordPositions = [];
    
    if (chordPlayMode === 'primary') {
      chordPositions = chordDefinitions[chordName];
    } else if (chordPlayMode === 'alternative') {
      chordPositions = alternativeChordPositions[chordName] || chordDefinitions[chordName];
    }

    const pattern = getChordPlayPattern(chordPlayPattern);
    console.log(`${chordName} 코드 연주 시작 (${chordPlayMode} 모드, ${chordPlayPattern} 패턴):`, chordPositions);

    // 스트러밍의 경우 더 빠른 속도 사용
    // 패턴별로 적절한 속도 설정
    const getPatternSpeed = (pattern, baseSpeed) => {
      const speedMultipliers = {
        // 기본 패턴
        'sequential': 1.0,
        'reverse': 1.0,
        'updown': 0.9,
        
        // 트래비스 피킹 (빠른 템포 필요)
        'travis_basic': 0.8,
        'travis_classic': 0.7,
        'travis_simple': 1.0,
        'travis_pinch': 0.9,
        
        // 아르페지오 (우아하게)
        'arpeggio_up': 1.2,
        'arpeggio_down': 1.2,
        'arpeggio_roll': 1.1,
        
        // PIMA 패턴
        'pima_basic': 1.3,
        'pima_reverse': 1.3,
        'pima_alternating': 1.0,
        
        // 장르별 (각 장르의 특성에 맞게)
        'waltz': 1.6, // 왈츠는 느리고 우아하게
        'country': 0.7, // 컨트리는 빠르게
        'folk': 1.1, // 포크는 편안하게
        'blues': 1.2, // 블루스는 여유롭게
        'ballad': 1.8, // 발라드는 가장 느리게
        'classical': 1.4, // 클래식은 정확하게
        
        // 고급 패턴
        'alternating_bass': 0.9,
        'syncopated': 0.8,
        'celtic': 0.6, // 켈틱은 매우 빠르게
        'flamenco': 0.5, // 플라멩코는 가장 빠르게
        
        // 스트러밍
        'strum_down': 0.2,
        'strum_updown': 0.3,
        'strum_folk': 0.4
      };
      
      return Math.round(baseSpeed * (speedMultipliers[pattern] || 1.0));
    };

    const playSpeed = getPatternSpeed(chordPlayPattern, chordPlaySpeed);

    pattern.forEach((guitarUtilsStringIndex, index) => {
      setTimeout(() => {
        const chordDefStringNumber = 6 - guitarUtilsStringIndex;
        const position = chordPositions.find(pos => pos.string === chordDefStringNumber);
        
        if (position) {
          const note = GuitarUtils.calculateNote(guitarUtilsStringIndex, position.fret);
          console.log(`코드 구성음 연주: ${note} (${7-chordDefStringNumber}현 ${position.fret}프렛)`);
          playSound(note, guitarUtilsStringIndex, 1.5);
        } else {
          const note = GuitarUtils.calculateNote(guitarUtilsStringIndex, 0);
          console.log(`개방현 연주: ${note} (${7-chordDefStringNumber}현 0프렛)`);
          playSound(note, guitarUtilsStringIndex, 2.0);
        }
      }, index * playSpeed);
    });

    console.log(`코드 연주: ${chordName} (${chordPlayMode}, ${chordPlayPattern})`);
  }, [isAudioReady, playSound, chordPlayMode, chordPlaySpeed, chordPlayPattern, getChordPlayPattern]);

   
  // 마우스 위치에서 소리 재생 (Shift 키용)
  const playAtMousePosition = useCallback(() => {
    if (!isAudioReady) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const transformedCoords = GuitarUtils.transformCoordinates(lastMousePosition.x, lastMousePosition.y, canvas, canvasTransform);
    const position = GuitarUtils.getFretAndString(transformedCoords.x, transformedCoords.y, canvas, MAXFRET);

    if (!position) return;

    const fret = position.fret;
    const string = position.string;
    const note = GuitarUtils.calculateNote(string, fret);

    setPressedFret(fret);
    setPressedString(string);

    setTimeout(() => {
      playSound(note, string, 1.5);
    }, 300);

    setTimeout(() => {
      if (!isMouseDown && !isShiftPressed) {
        setPressedFret(null);
        setPressedString(null);
      }
    }, 300);
  }, [isAudioReady, isShiftPressed, lastMousePosition, isMouseDown, canvasTransform, MAXFRET, playSound]);

  // 이벤트 핸들러들
  const handleCanvasMouseMove = useCallback((event) => {
    setLastMousePosition({ x: event.clientX, y: event.clientY });
    if (isShiftPressed) {
      playAtMousePosition();
    }
  }, [isShiftPressed, playAtMousePosition]);

  const handleCanvasMouseDown = useCallback((event) => {
    if (!isAudioReady) return;

    const canvas = canvasRef.current;
    const transformedCoords = GuitarUtils.transformCoordinates(event.clientX, event.clientY, canvas, canvasTransform);
    const position = GuitarUtils.getFretAndString(transformedCoords.x, transformedCoords.y, canvas, MAXFRET);

    if (!position) return;

    setIsMouseDown(true);
    setPressedFret(position.fret);
    setPressedString(position.string);

    const currentNote = GuitarUtils.calculateNote(position.string, position.fret);
    playSound(currentNote, position.string, 1.5);

    // 코드가 선택된 상태에서 마우스 클릭 시에도 개별 음표가 연주되도록 변경
    // playChord 호출은 별도 버튼이나 키보드 이벤트로 처리
  }, [isAudioReady, canvasTransform, MAXFRET, playSound]);

  const handleCanvasMouseUp = useCallback(() => {
    if (!isAudioReady || !isMouseDown) return;

    setIsMouseDown(false);
    stopSound();

    setTimeout(() => {
      setPressedFret(null);
      setPressedString(null);
    }, 100);
  }, [isAudioReady, isMouseDown, stopSound]);

  const handleCanvasMouseLeave = useCallback(() => {
    if (isMouseDown) {
      setIsMouseDown(false);
      stopSound();

      setTimeout(() => {
        setPressedFret(null);
        setPressedString(null);
      }, 100);
    }
  }, [isMouseDown, stopSound]);

  const handleVolumeChange = useCallback((e) => {
    const newVolume = e.target.value;
    setVolume(newVolume);

    if (masterGainRef.current) {
      masterGainRef.current.gain.value = (newVolume / 100) * 0.5;
    }
  }, []);

  const handleCategoryChange = useCallback((category) => {
    setActiveChordCategory(category);
    setSelectedChord(null);
  }, []);

  const handleChordSelect = useCallback((chordName) => {
    setSelectedChord(chordName);
    playChord(chordName);
  }, [playChord]);

  // 키보드 이벤트 핸들러
  const handleKeyDown = useCallback((event) => {
    if (!isAudioReady) return;

    const key = event.key;

    if (key === 'Shift') {
      if (!isShiftPressed) {
        setIsShiftPressed(true);
        playAtMousePosition();
      }
      return;
    }

    if (selectedChord) {
      return;
    }

    if (key >= '1' && key <= '6') {
      const stringIndex = parseInt(key) - 1;
      const note = stringNotes[stringIndex];

      setPressedFret(0);
      setPressedString(stringIndex);

      const displayText = GuitarUtils.updateNoteDisplay(note, stringIndex, 0, selectedChord, GuitarUtils.getKoreanNoteName);
      setNoteDisplay(displayText);
      playSound(note, stringIndex);

      setTimeout(() => {
        if (!isMouseDown) {
          setPressedFret(null);
          setPressedString(null);
        }
      }, 300);
    }
  }, [isAudioReady, isShiftPressed, isMouseDown, playAtMousePosition, selectedChord, playSound]);

  const handleKeyUp = useCallback((event) => {
    if (event.key === 'Shift') {
      setIsShiftPressed(false);
      stopSound();
    }
  }, [stopSound]);

  // Guitar Canvas 초기화
  useEffect(() => {
    if (isAudioReady) {
      guitarCanvasRef.current = new GuitarCanvas(canvasRef, {
        showNotes,
        selectedChord,
        pressedFret,
        pressedString,
        canvasTransform,
        maxFret: MAXFRET,
        isExternalInput,
        noteIntensities,
        chordDefinitions,
        alternativeChordPositions,
        useKoreanNotes,
        showFingerNumbers
      });
      guitarCanvasRef.current.initCanvas();
    }
  }, [isAudioReady, MAXFRET]);

  // Canvas 업데이트
  useEffect(() => {
    if (guitarCanvasRef.current) {
      guitarCanvasRef.current.updateConfig({
        showNotes,
        selectedChord,
        pressedFret,
        pressedString,
        canvasTransform,
        isExternalInput,
        noteIntensities,
        chordDefinitions,
        useKoreanNotes,
        showFingerNumbers,
        chordPlayMode
      });
      guitarCanvasRef.current.drawFretboard();
    }
  }, [showNotes, selectedChord, pressedFret, pressedString, canvasTransform, isExternalInput, noteIntensities, useKoreanNotes, showFingerNumbers, chordPlayMode]);

  // 진동 애니메이션을 위한 렌더링 루프
  useEffect(() => {
    let animationId;
    
    const animate = () => {
      if (guitarCanvasRef.current && guitarCanvasRef.current.config.vibratingStrings.size > 0) {
        guitarCanvasRef.current.drawFretboard();
      }
      animationId = requestAnimationFrame(animate);
    };
    
    animate();
    
    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, [isAudioReady]);

  // 키보드 이벤트 리스너
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keyup', handleKeyUp);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keyup', handleKeyUp);
    };
  }, [handleKeyDown, handleKeyUp]);

  // 윈도우 리사이즈 이벤트
  useEffect(() => {
    const handleResize = () => {
      if (guitarCanvasRef.current) {
        guitarCanvasRef.current.initCanvas();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Cleanup
  useEffect(() => {
    return () => {
      if (audioAnalyzer) {
        audioAnalyzer.stop();
      }
    };
  }, [audioAnalyzer]);

  return (
    <div className="guitar-simulator-container">
      {!isAudioReady ? (
        <div className="guitar-simulator-content1">
          <h1 className="guitar-title">
            🎸 {language === 'KR' ? '온라인 기타 코드 학습기' : 'Online Guitar Chord Learner'}
          </h1>
          <button
            onClick={initAudio}
            className="start-button"
            style={{width: '300px'}}
          >
            🎵 {language === 'KR' ? '오디오 시작하기' : 'Start Audio'}
          </button>

          <div className="status">
            {audioStatus}
          </div>
        </div>
      ) : (
        <div className="guitar-simulator-content2">
           <h1 className="guitar-title">
            🎸 {language === 'KR' ? '온라인 기타 코드 학습기' : 'Online Guitar Chord Learner'}
          </h1>
          
          <div className="controls">
            <div className="volume-control">
              <span>🔊 </span>
              <input
                type="range"
                min="0"
                max="100"
                value={volume}
                onChange={handleVolumeChange}
                className="vertical-slider"  
              />
              <span>{volume}%</span>
            </div>
            
            {/* 기타 사운드 테스트 버튼 추가 */}
            {/* <button 
              onClick={() => playSound('A4', 0, 1)}
              className="test-btn"
              disabled={!isAudioReady}
            >
              🎵 {language === 'KR' ? '테스트 사운드' : 'Test Sound'}
            </button> */}
            
            <button
              onClick={() => setShowNotes(!showNotes)}
              className={`test-btn ${showNotes ? 'active' : ''}`}
            >
              🎵 {showNotes ? (language === 'KR' ? '음표 숨기기' : 'Hide Notes') : (language === 'KR' ? '음표 표시' : 'Show Notes')}
            </button>

            <button
              onClick={() => setCanvasTransform(prev => ({...prev, rotation: prev.rotation === 0 ? 180 : 0}))}
              className={`test-btn ${canvasTransform.rotation === 180 ? 'active' : ''}`}
            >
              🔄 {language === 'KR' ? '180도 회전' : '180° Rotate'}
            </button>

            <button
              onClick={() => setCanvasTransform(prev => ({...prev, flipX: !prev.flipX}))}
              className={`test-btn ${canvasTransform.flipX ? 'active' : ''}`}
            >
              ↔️ {language === 'KR' ? '좌우반전' : 'Flip Horizontal'}
            </button>

            <button
              onClick={() => setUseKoreanNotes(!useKoreanNotes)}
              className={`test-btn ${useKoreanNotes ? 'active' : ''}`}
            >
              🎵 {useKoreanNotes ? (language === 'KR' ? '영문 음계' : 'English Notes') : (language === 'KR' ? '한글 음계' : 'Korean Notes')}
            </button>

            <button
              onClick={() => setShowFingerNumbers(!showFingerNumbers)}
              className={`test-btn ${showFingerNumbers ? 'active' : ''}`}
            >
              ✋ {showFingerNumbers ? (language === 'KR' ? '음계 표시' : 'Show Notes') : (language === 'KR' ? '손가락 번호' : 'Finger Numbers')}
            </button>
 
            

            {/* 스펙트럼 디스플레이 토글 버튼 */}
            {isExternalInput && (
              <button
                onClick={() => setShowSpectrum(!showSpectrum)}
                className={`test-btn ${showSpectrum ? 'active' : ''}`}
              >
                📊 {language === 'KR' ? '스펙트럼' : 'Spectrum'}
              </button>
            )}
          </div>

          

          <div className="status" style={{marginBottom: '10px'}}>
            {audioStatus}
            {isAudioReady && audioContextRef.current && (
              <div style={{fontSize: '12px', color: '#666', marginTop: '5px'}}>
                오디오 컨텍스트 상태: {audioContextRef.current.state} | 
                샘플레이트: {audioContextRef.current.sampleRate}Hz
                {isExternalInput && ` | 외부입력: ${inputSource}`}
              </div>
            )}
          </div>

          <div className="note-display" style={{marginBottom: '10px'}}>
            {noteDisplay}
          </div>

          <canvas
            ref={canvasRef}
            className="guitar-canvas"
            onMouseMove={handleCanvasMouseMove}
            onMouseDown={handleCanvasMouseDown}
            onMouseUp={handleCanvasMouseUp}
            onMouseLeave={handleCanvasMouseLeave}
          />

          {/* 주파수 스펙트럼 디스플레이 */}
          {showSpectrum && isExternalInput && (
            <FrequencySpectrumDisplay
              audioAnalyzer={audioAnalyzer}
              isActive={isExternalInput}
              language={language}
              height={200}
              showGuitarFrequencies={true}
            />
          )}

          <div className="chord-section">
            <div className="chord-categories">
              {Object.keys(chordCategories).map(category => (
                <button
                  key={category}
                  className={`category-btn ${activeChordCategory === category ? 'active' : ''}`}
                  onClick={() => handleCategoryChange(category)}
                >
                  {chordCategories[category].title}
                </button>
              ))}
            </div>

            <div className="chord-group">
              <div className="chord-buttons">
                {chordCategories[activeChordCategory].chords.map(chord => (
                  <button
                    key={chord}
                    className={`chord-btn ${selectedChord === chord ? 'active' : ''}`}
                    onClick={() => handleChordSelect(chord)}
                  >
                    {chord}
                  </button>
                ))}
              </div>
            </div> 

            <div className="sound-type-controls">
              <span style={{color: '#f9f8f8ff'}}>{language === 'KR' ? '🎵 사운드:' : '🎵 Sound:'}</span>
              <select
                value={soundType}
                onChange={(e) => setSoundType(e.target.value)}
                className="sound-type-selector"
              >
                <option value="guitar">{language === 'KR' ? '기타' : 'Guitar'}</option>
                <option value="acoustic">{language === 'KR' ? '통기타' : 'Acoustic'}</option>
                <option value="piano">{language === 'KR' ? '피아노' : 'Piano'}</option>
                <option value="violin">{language === 'KR' ? '바이올린' : 'Violin'}</option>
                <option value="organ">{language === 'KR' ? '오르간' : 'Organ'}</option>
                <option value="synth">{language === 'KR' ? '신스' : 'Synth'}</option>
              </select>
            </div>

            {/* 코드 포지션 선택 버튼들 */}
            <div className="chord-play-mode-controls">
              <span>{language === 'KR' ? '코드 포지션:' : 'Chord Position:'}</span>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={chordPlayMode === 'alternative'}
                  onChange={(e) => setChordPlayMode(e.target.checked ? 'alternative' : 'primary')}
                />
                <span className="toggle-slider">
                  <span className="toggle-label-right">
                    {language === 'KR' ? '대체 포지션' : 'Alt Position'}
                  </span>
                </span>
              </label>
              
              <div className="pattern-selector">
                <span>{language === 'KR' ? '연주 패턴:' : 'Play Pattern:'}</span>
                <select
                  value={chordPlayPattern}
                  onChange={(e) => setChordPlayPattern(e.target.value)}
                  className="pattern-selector-dropdown"
                >
                  <optgroup label={language === 'KR' ? '기본 패턴' : 'Basic Patterns'}>
                    <option value="sequential">{language === 'KR' ? '순차 연주' : 'Sequential'}</option>
                    <option value="reverse">{language === 'KR' ? '역순 연주' : 'Reverse'}</option>
                    <option value="updown">{language === 'KR' ? '상행-하행' : 'Up-Down'}</option>
                  </optgroup>
                  
                  <optgroup label={language === 'KR' ? '트래비스 피킹' : 'Travis Picking'}>
                    <option value="travis_basic">{language === 'KR' ? '기본 트래비스' : 'Basic Travis'}</option>
                    <option value="travis_classic">{language === 'KR' ? '클래식 트래비스' : 'Classic Travis'}</option>
                    <option value="travis_simple">{language === 'KR' ? '단순 트래비스' : 'Simple Travis'}</option>
                    <option value="travis_pinch">{language === 'KR' ? '트래비스 핀치' : 'Travis Pinch'}</option>
                  </optgroup>
                  
                  <optgroup label={language === 'KR' ? '아르페지오' : 'Arpeggio'}>
                    <option value="arpeggio_up">{language === 'KR' ? '상승 아르페지오' : 'Ascending Arpeggio'}</option>
                    <option value="arpeggio_down">{language === 'KR' ? '하강 아르페지오' : 'Descending Arpeggio'}</option>
                    <option value="arpeggio_roll">{language === 'KR' ? '롤링 아르페지오' : 'Rolling Arpeggio'}</option>
                  </optgroup>
                  
                  <optgroup label={language === 'KR' ? 'PIMA 패턴' : 'PIMA Patterns'}>
                    <option value="pima_basic">{language === 'KR' ? '기본 PIMA' : 'Basic PIMA'}</option>
                    <option value="pima_reverse">{language === 'KR' ? '역순 PIMA' : 'Reverse PIMA'}</option>
                    <option value="pima_alternating">{language === 'KR' ? '교대 PIMA' : 'Alternating PIMA'}</option>
                  </optgroup>
                  
                  <optgroup label={language === 'KR' ? '장르별 패턴' : 'Genre Patterns'}>
                    <option value="waltz">{language === 'KR' ? '왈츠 (3/4박자)' : 'Waltz (3/4 Time)'}</option>
                    <option value="country">{language === 'KR' ? '컨트리' : 'Country'}</option>
                    <option value="folk">{language === 'KR' ? '포크' : 'Folk'}</option>
                    <option value="blues">{language === 'KR' ? '블루스' : 'Blues'}</option>
                    <option value="ballad">{language === 'KR' ? '발라드' : 'Ballad'}</option>
                    <option value="classical">{language === 'KR' ? '클래식' : 'Classical'}</option>
                  </optgroup>
                  
                  <optgroup label={language === 'KR' ? '고급 패턴' : 'Advanced Patterns'}>
                    <option value="alternating_bass">{language === 'KR' ? '교대 베이스' : 'Alternating Bass'}</option>
                    <option value="syncopated">{language === 'KR' ? '싱코페이션' : 'Syncopated'}</option>
                    <option value="celtic">{language === 'KR' ? '켈틱' : 'Celtic'}</option>
                    <option value="flamenco">{language === 'KR' ? '플라멩코' : 'Flamenco'}</option>
                  </optgroup>
                  
                  <optgroup label={language === 'KR' ? '스트러밍' : 'Strumming'}>
                    <option value="strum_down">{language === 'KR' ? '다운 스트럼' : 'Down Strum'}</option>
                    <option value="strum_updown">{language === 'KR' ? '업다운 스트럼' : 'Up-Down Strum'}</option>
                    <option value="strum_folk">{language === 'KR' ? '포크 스트럼' : 'Folk Strum'}</option>
                  </optgroup>
                </select>
              </div>
              
              <div className="speed-controls">
                <span>{language === 'KR' ? '연주 속도:' : 'Play Speed:'}</span>
                <input
                  type="range"
                  min="100"
                  max="800"
                  value={chordPlaySpeed}
                  onChange={(e) => setChordPlaySpeed(parseInt(e.target.value))}
                  className="speed-slider"
                />
                <span>{chordPlaySpeed}ms</span>
                <button
                  onClick={() => setChordPlaySpeed(150)}
                  className="speed-preset-btn"
                >
                  {language === 'KR' ? '빠름' : 'Fast'}
                </button>
                <button
                  onClick={() => setChordPlaySpeed(300)}
                  className="speed-preset-btn"
                >
                  {language === 'KR' ? '보통' : 'Normal'}
                </button>
                <button
                  onClick={() => setChordPlaySpeed(500)}
                  className="speed-preset-btn"
                >
                  {language === 'KR' ? '느림' : 'Slow'}
                </button>
              </div>
            </div>
 

            {/* <div className="speed-control">
              <label className="speed-checkbox">
                <input
                  type="checkbox"
                  checked={chordPlaySpeed === 30}
                  onChange={(e) => setChordPlaySpeed(e.target.checked ? 30: 150)}
                />
                <span style={{color: '#f9f8f8ff'}}>{language === 'KR' ? '빠른 스윙' : 'Fast Swing'}</span>
              </label>
            </div> */}
          </div>

          <div className="ex-info">
            {language === 'KR' ? '마이크 입력 또는 mp3 파일을 선택하세요.' : 'Select microphone input or an mp3 file.'} 
          </div>

          {/* 외부 입력 컨트롤 */}
          <ExternalInputControls
            inputSource={inputSource}
            isExternalInput={isExternalInput}
            language={language}
            audioAnalyzer={audioAnalyzer}
            onMicrophoneToggle={handleMicrophoneToggle}
            onFileUpload={initFileInput}
            onStopInput={stopExternalInput}
          />

          <div className="instructions">
            <strong>{language === 'KR' ? '사용법' : 'Instructions'}:</strong><br/>
            • <strong>Shift {language === 'KR' ? '키' : 'Key'}:</strong> {language === 'KR' ? '마우스 위치에서 Shift 키를 누르고 있으면 누르는 시간만큼 소리가 지속됩니다' : 'Hold Shift at mouse position to sustain sound'}<br/>
            • <strong>{language === 'KR' ? '숫자 키' : 'Number Keys'}:</strong> {language === 'KR' ? '1-6 키로 각 현의 개방현을 연주할 수 있습니다' : 'Use 1-6 keys to play open strings'}<br/>
            • <strong>{language === 'KR' ? '외부 입력' : 'External Input'}:</strong> {language === 'KR' ? '마이크나 오디오 파일로 실시간 주파수 분석이 가능합니다' : 'Real-time frequency analysis via microphone or audio file'}<br/>
            • <strong>{language === 'KR' ? '스펙트럼 표시' : 'Spectrum Display'}:</strong> {language === 'KR' ? '외부 입력 활성화 시 주파수 스펙트럼을 실시간으로 볼 수 있습니다' : 'Real-time frequency spectrum visualization when external input is active'}<br/>
            • <strong>{language === 'KR' ? '음표 표시' : 'Note Display'}:</strong> {language === 'KR' ? '각 위치에 도레미파솔라시도가 영어 알파벳(C, D, E, F, G, A, B)으로 표시됩니다' : 'Each position shows notes as English alphabet (C, D, E, F, G, A, B)'}<br/>
            • <strong>{language === 'KR' ? '코드 연주' : 'Chord Playing'}:</strong> {language === 'KR' ? '코드를 선택하고 Shift 키를 누르면 선택된 코드의 구성음이 강조되고 연주됩니다' : 'Select a chord and hold Shift to highlight and play chord notes'}<br/>
            • <strong>{language === 'KR' ? '민감도 조절' : 'Sensitivity Control'}:</strong> {language === 'KR' ? '외부 입력 시 민감도 슬라이더로 작은 소리 필터링 정도를 조절할 수 있습니다' : 'Adjust sensitivity slider to filter weak signals during external input'}<br/>
            • <strong>{language === 'KR' ? '코드 연주 모드' : 'Chord Play Mode'}:</strong> {language === 'KR' ? '기본 포지션 또는 대체 포지션을 선택하여 코드를 연주할 수 있습니다' : 'Choose primary position, alternative position, or both to play chords'}<br/>
          </div>
        </div>
      )}
    </div>
  );
};

export default GuitarSimulator;